import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Camera, Upload, Loader2, Check, X, Trash2, Eye, EyeOff, Plus, Share2, Image as ImageIcon, Barcode, ScanLine } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import ShareButtons from '../components/ShareButtons';
import TagOrderManager from '../components/TagOrderManager';
import RecoveryDialog from '../components/RecoveryDialog';
import StockEditorDialog from '../components/StockEditorDialog';
import ImageSearchButton from '../components/ImageSearchButton';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = [
  { value: 'biancheria', label: 'Biancheria' },
  { value: 'casalinghi', label: 'Casalinghi' },
  { value: 'pigiameria', label: 'Pigiameria' },
  { value: 'trapunte', label: 'Trapunte' },
  { value: 'tappeti', label: 'Tappeti' },
  { value: 'oggettistica', label: 'Oggettistica' },
  { value: 'regalistica', label: 'Regalistica' },
  { value: 'altro', label: 'Altro' },
];

export default function AdminPage() {
  const { user, isAdmin, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [activeTab, setActiveTab] = useState('upload');
  const [uploading, setUploading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [draftProduct, setDraftProduct] = useState(null);
  const [products, setProducts] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [shopCategories, setShopCategories] = useState([]);
  const [barcodeInput, setBarcodeInput] = useState('');
  const [barcodeLoading, setBarcodeLoading] = useState(false);
  const [todayFlyer, setTodayFlyer] = useState(null);
  const [uploadMode, setUploadMode] = useState('photo'); // 'photo' or 'barcode' or 'batch'
  const [showPreview, setShowPreview] = useState(false);
  const [previewImgSrc, setPreviewImgSrc] = useState(null);

  // Guided multi-shot state
  const shotInputRef = useRef(null);
  const multiUploadRef = useRef(null);
  const [shotFlowActive, setShotFlowActive] = useState(false);
  const [shotPhase, setShotPhase] = useState('capture'); // 'capture' | 'review' | 'validate'
  const [shots, setShots] = useState([]); // {file, preview}[]
  const [pendingShot, setPendingShot] = useState(null); // {file, preview}
  const [userPrompt, setUserPrompt] = useState('');
  const [multiProductMode, setMultiProductMode] = useState(false); // true = each photo is a separate product
  const [batchProgress, setBatchProgress] = useState(null); // {current, total}
  const [draftQueue, setDraftQueue] = useState([]); // queued drafts awaiting review (after batch multi-product)
  const [aiPlanStatus, setAiPlanStatus] = useState(null);
  const [tagOrderOpen, setTagOrderOpen] = useState(false);
  const [productsSearch, setProductsSearch] = useState('');
  const [productsStatusFilter, setProductsStatusFilter] = useState('all');
  const [editingProduct, setEditingProduct] = useState(null);
  const [previewingProduct, setPreviewingProduct] = useState(null);
  const [stockEditProduct, setStockEditProduct] = useState(null);
  const [recoveryOpen, setRecoveryOpen] = useState(false);
  const [shopSlug, setShopSlug] = useState(null);
  const [shop, setShop] = useState(null);

  const getHeaders = () => {
    const headers = {};
    if (user?.role === 'superadmin') {
      const activeShop = localStorage.getItem('phv_active_shop');
      if (activeShop) headers['X-Shop-Id'] = activeShop;
    }
    return headers;
  };

  useEffect(() => {
    if (authLoading) return; // wait for auth hydration
    if (!isAdmin) {
      navigate('/account');
      return;
    }
    fetchProducts();
    fetchCategories();
    fetchTodayFlyer();
    fetchAiPlanStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin, authLoading, navigate]);

  const fetchAiPlanStatus = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/ai-plan-status`, { withCredentials: true, headers: getHeaders() });
      setAiPlanStatus(data);
    } catch { /* ignore */ }
  };

  const fetchCategories = async () => {
    try {
      const { data } = await axios.get(`${API}/categories`, { withCredentials: true, headers: getHeaders() });
      setShopCategories(data.categories || []);
    } catch {}
  };

  const fetchTodayFlyer = async () => {
    try {
      const { data } = await axios.get(`${API}/flyers`, { withCredentials: true, headers: getHeaders(), params: { limit: 1 } });
      if (data.flyers?.length > 0) setTodayFlyer(data.flyers[0]);
    } catch {}
  };

  const handleBarcodeLookup = async () => {
    if (!barcodeInput.trim()) return;
    setBarcodeLoading(true);
    try {
      const { data } = await axios.post(`${API}/barcode/lookup`, { barcode: barcodeInput }, { withCredentials: true, headers: getHeaders() });
      if (data.found) {
        setDraftProduct({
          id: null,
          title: data.title || '',
          description: data.description || data.category || '',
          price: 0,
          original_price: null,
          category: 'Altro',
          variants: [],
          images: [],
          status: 'draft',
          ai_raw: `Barcode: ${barcodeInput} | Brand: ${data.brand || ''} | Source: ${data.source}`,
          barcode_data: data
        });
        toast.success('Prodotto trovato! Completa i dati e carica una foto.');
      } else {
        toast.error('Barcode non trovato. Usa la foto per creare il prodotto.');
      }
    } catch (e) {
      toast.error('Errore ricerca barcode');
    } finally {
      setBarcodeLoading(false);
    }
  };

  const handleBatchUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    setAnalyzing(true);
    try {
      const formData = new FormData();
      files.slice(0, 10).forEach(f => formData.append('files', f));
      const { data } = await axios.post(`${API}/products/batch-upload`, formData, {
        withCredentials: true,
        headers: { ...getHeaders(), 'Content-Type': 'multipart/form-data' }
      });
      toast.success(`${data.success}/${data.total} prodotti creati! Vai ai Prodotti per modificarli.`);
      fetchProducts();
      fetchTodayFlyer();
      setActiveTab('products');
    } catch (e) {
      toast.error('Errore upload multiplo');
    } finally {
      setUploading(false);
      setAnalyzing(false);
    }
  };

  const fetchProducts = async () => {
    setLoadingProducts(true);
    try {
      const params = {};
      if (productsSearch?.trim()) params.search = productsSearch.trim();
      if (productsStatusFilter && productsStatusFilter !== 'all') params.status = productsStatusFilter;
      const { data } = await axios.get(`${API}/products/admin/all`, { withCredentials: true, headers: getHeaders(), params });
      setProducts(data.products || []);
    } catch (e) {
      console.error('Errore caricamento prodotti admin:', e);
    } finally {
      setLoadingProducts(false);
    }
  };

  // Re-fetch products when filters change (debounced)
  useEffect(() => {
    if (!isAdmin) return;
    const t = setTimeout(() => { fetchProducts(); }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productsSearch, productsStatusFilter]);

  // Get shop slug for "view as customer" button
  useEffect(() => {
    if (!isAdmin) return;
    axios.get(`${API}/shops/mine`, { withCredentials: true, headers: getHeaders() })
      .then(r => {
        setShopSlug(r.data?.slug || null);
        setShop(r.data || null);
      })
      .catch(() => {});
  }, [isAdmin]);

  const addToTodayFlyer = async (productId) => {
    try {
      const { data } = await axios.post(`${API}/flyers/today/add-products`,
        { product_ids: [productId] },
        { withCredentials: true, headers: getHeaders() });
      if (data.added > 0) {
        toast.success('Aggiunto al volantino di oggi');
        fetchTodayFlyer();
      } else if (data.skipped_drafts > 0) {
        toast.warning('Pubblica prima la bozza per aggiungerla al volantino');
      } else {
        toast.info('Già presente nel volantino di oggi');
      }
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Errore aggiunta al volantino';
      toast.error(msg);
    }
  };

  const openProductPreview = (product) => {
    setPreviewingProduct(product);
  };

  const openProductEdit = (product) => {
    setEditingProduct({ ...product });
  };

  const saveProductEdit = async () => {
    if (!editingProduct?.id) return;
    try {
      const payload = {
        title: editingProduct.title,
        description: editingProduct.description,
        price: parseFloat(editingProduct.price) || 0,
        original_price: editingProduct.original_price ? parseFloat(editingProduct.original_price) : null,
        category: editingProduct.category,
        tags: editingProduct.tags || [],
        variants: editingProduct.variants || [],
      };
      await axios.put(`${API}/products/${editingProduct.id}`, payload, { withCredentials: true, headers: getHeaders() });
      toast.success('Prodotto aggiornato');
      setEditingProduct(null);
      fetchProducts();
    } catch (e) {
      toast.error('Errore aggiornamento');
    }
  };

  const cropToSquare = (file) => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          const side = Math.min(img.width, img.height);
          const sx = Math.floor((img.width - side) / 2);
          const sy = Math.floor((img.height - side) / 2);
          const canvas = document.createElement('canvas');
          canvas.width = side;
          canvas.height = side;
          canvas.getContext('2d').drawImage(img, sx, sy, side, side, 0, 0, side, side);
          canvas.toBlob((blob) => {
            resolve(new File([blob], file.name, { type: 'image/jpeg' }));
          }, 'image/jpeg', 0.92);
        };
        img.src = e.target.result;
      };
      reader.readAsDataURL(file);
    });
  };

  const compressImage = (file, maxWidth = 1200, quality = 0.8) => {
    return new Promise((resolve) => {
      // If file is small enough, skip compression
      if (file.size < 500000) { resolve(file); return; }

      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          let w = img.width, h = img.height;
          if (w > maxWidth) { h = (h * maxWidth) / w; w = maxWidth; }
          canvas.width = w;
          canvas.height = h;
          canvas.getContext('2d').drawImage(img, 0, 0, w, h);
          canvas.toBlob((blob) => {
            resolve(new File([blob], file.name, { type: 'image/jpeg' }));
          }, 'image/jpeg', quality);
        };
        img.src = e.target.result;
      };
      reader.readAsDataURL(file);
    });
  };

  const handleMultiUpload = async (e) => {
    const fileList = Array.from(e.target.files || []);
    if (!fileList.length) return;

    setUploading(true);
    setAnalyzing(true);
    setDraftProduct(null);

    try {
      const formData = new FormData();
      for (const file of fileList.slice(0, 6)) {
        const compressed = await compressImage(file);
        formData.append('files', compressed);
      }

      const { data } = await axios.post(`${API}/products/upload-and-analyze`, formData, {
        withCredentials: true,
        headers: { ...getHeaders(), 'Content-Type': 'multipart/form-data' },
        timeout: 180000
      });
      setDraftProduct(data);
      toast.success(`${data.photo_count || fileList.length} foto analizzate! Prodotto creato.`);
    } catch (e) {
      console.error('Upload error:', e);
      if (e.code === 'ECONNABORTED' || e.message?.includes('timeout')) {
        toast.error('Analisi troppo lenta. Riprova con meno foto.');
      } else if (e.response?.status === 401) {
        toast.error('Sessione scaduta. Effettua di nuovo il login.');
      } else {
        toast.error('Errore durante l\'analisi. Riprova.');
      }
    } finally {
      setUploading(false);
      setAnalyzing(false);
    }
  };

  // ======== Guided multi-shot camera flow ========
  const startShotFlow = () => {
    setShots([]);
    setPendingShot(null);
    setUserPrompt('');
    setShotPhase('capture');
    setShotFlowActive(true);
    setTimeout(() => shotInputRef.current?.click(), 50);
  };

  const onShotTaken = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    // Crop to square (1:1) so AI sees the same framing the user will see
    const squared = await cropToSquare(file);
    const preview = URL.createObjectURL(squared);
    setPendingShot({ file: squared, preview });
    setShotPhase('review');
  };

  const confirmPendingShot = () => {
    if (!pendingShot) return;
    const next = [...shots, pendingShot];
    setShots(next);
    setPendingShot(null);
    // Max reached → auto-advance. Otherwise stay in capture UI so the user
    // can decide between "Altra foto" or "Termina ora" — never auto-reopen camera.
    if (next.length >= 6) {
      setShotPhase('validate');
    } else {
      setShotPhase('capture');
    }
  };

  const retryPendingShot = () => {
    if (pendingShot?.preview) URL.revokeObjectURL(pendingShot.preview);
    setPendingShot(null);
    setShotPhase('capture');
    setTimeout(() => shotInputRef.current?.click(), 50);
  };

  // Multi-file upload from gallery (alternative to live camera within Scatto Guidato)
  const onMultiPhotosUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (!files.length) return;
    const remaining = Math.max(0, 6 - shots.length);
    if (remaining === 0) {
      toast.info('Hai già 6 foto. Rimuovine qualcuna per aggiungerne.');
      return;
    }
    const slice = files.slice(0, remaining);
    const newShots = [];
    for (const f of slice) {
      try {
        const squared = await cropToSquare(f);
        newShots.push({ file: squared, preview: URL.createObjectURL(squared) });
      } catch (_) { /* skip bad file */ }
    }
    if (!newShots.length) return;
    const next = [...shots, ...newShots];
    setShots(next);
    setPendingShot(null);
    // Advance straight to validate so the user sees AI prompt + Termina button
    setShotPhase('validate');
    if (files.length > remaining) {
      toast.info(`Aggiunte ${slice.length} foto (limite 6). ${files.length - remaining} ignorate.`);
    } else {
      toast.success(`${slice.length} foto caricate. Premi Termina per analizzarle.`);
    }
  };

  const discardPendingShot = () => {
    if (pendingShot?.preview) URL.revokeObjectURL(pendingShot.preview);
    setPendingShot(null);
    setShotPhase('capture');
  };

  const finishShotFlow = () => {
    // If there is a pending shot in review, confirm it before terminating
    // so the user never loses the last photo when tapping "Termina".
    let total = shots.length;
    if (pendingShot) {
      setShots(prev => [...prev, pendingShot]);
      total += 1;
      setPendingShot(null);
    }
    if (total === 0) {
      toast.error('Scatta almeno una foto prima di terminare');
      setShotPhase('capture');
      return;
    }
    setShotPhase('validate');
  };

  const removeShot = (idx) => {
    const next = shots.filter((_, i) => i !== idx);
    if (shots[idx]?.preview) URL.revokeObjectURL(shots[idx].preview);
    setShots(next);
  };

  const cancelShotFlow = () => {
    shots.forEach(s => s.preview && URL.revokeObjectURL(s.preview));
    if (pendingShot?.preview) URL.revokeObjectURL(pendingShot.preview);
    setShots([]);
    setPendingShot(null);
    setUserPrompt('');
    setShotFlowActive(false);
    setShotPhase('capture');
    setMultiProductMode(false);
    setBatchProgress(null);
  };

  const submitShotFlow = async () => {
    if (!shots.length) return;

    // ── BRANCH 1: each photo is a separate product ──
    if (multiProductMode && shots.length > 1) {
      setUploading(true);
      setAnalyzing(true);
      setBatchProgress({ current: 0, total: shots.length });
      const created = [];
      const failed = [];
      for (let i = 0; i < shots.length; i++) {
        setBatchProgress({ current: i + 1, total: shots.length });
        try {
          const fd = new FormData();
          const compressed = await compressImage(shots[i].file);
          fd.append('files', compressed);
          if (userPrompt.trim()) fd.append('user_prompt', userPrompt.trim());
          fd.append('generate_ai_images', 'true');
          const { data } = await axios.post(`${API}/products/upload-and-analyze`, fd, {
            withCredentials: true,
            headers: { ...getHeaders(), 'Content-Type': 'multipart/form-data' },
            timeout: 240000,
          });
          created.push(data);
        } catch (e) {
          failed.push({ index: i, message: e.response?.data?.detail || e.message });
        }
      }
      shots.forEach(s => s.preview && URL.revokeObjectURL(s.preview));
      setShots([]);
      setUserPrompt('');
      setShotFlowActive(false);
      setShotPhase('capture');
      setMultiProductMode(false);
      setBatchProgress(null);
      setUploading(false);
      setAnalyzing(false);
      if (created.length) {
        // Queue drafts for sequential review: first one opens immediately in DraftEditor,
        // the rest waits until user publishes / saves / discards the current
        const [first, ...rest] = created;
        setDraftQueue(rest);
        setDraftProduct(first);
        toast.success(`${created.length} prodotti elaborati${failed.length ? ` (${failed.length} falliti)` : ''}. Controlla e modifica prima di pubblicare.`);
      } else {
        toast.error(`Nessun prodotto creato. ${failed[0]?.message || ''}`);
      }
      return;
    }

    // ── BRANCH 2 (default): all photos belong to the same product ──
    setUploading(true);
    setAnalyzing(true);
    setDraftProduct(null);
    try {
      const formData = new FormData();
      for (const s of shots.slice(0, 6)) {
        const compressed = await compressImage(s.file);
        formData.append('files', compressed);
      }
      if (userPrompt.trim()) formData.append('user_prompt', userPrompt.trim());
      formData.append('generate_ai_images', 'true');

      const { data } = await axios.post(`${API}/products/upload-and-analyze`, formData, {
        withCredentials: true,
        headers: { ...getHeaders(), 'Content-Type': 'multipart/form-data' },
        timeout: 240000
      });
      setDraftProduct(data);
      shots.forEach(s => s.preview && URL.revokeObjectURL(s.preview));
      setShots([]);
      setUserPrompt('');
      setShotFlowActive(false);
      setShotPhase('capture');
      if (data.ai_generation_error) {
        toast.warning(`Foto salvate, ma AI ha dato errore: ${data.ai_generation_error.slice(0, 120)}`);
      } else if (data.ai_images_generated) {
        toast.success(`${data.photo_count || shots.length} foto elaborate con AI!`);
      } else {
        toast.success(`${data.photo_count || shots.length} foto caricate!`);
      }
    } catch (e) {
      console.error('Upload error:', e);
      if (e.code === 'ECONNABORTED' || e.message?.includes('timeout')) {
        toast.error('Elaborazione troppo lenta. Riprova con meno foto o istruzioni più brevi.');
      } else if (e.response?.status === 401) {
        toast.error('Sessione scaduta. Effettua di nuovo il login.');
      } else {
        toast.error('Errore elaborazione AI. Riprova.');
      }
    } finally {
      setUploading(false);
      setAnalyzing(false);
    }
  };

  const regenerateAiImages = async () => {
    if (!draftProduct?.id) return;
    setAnalyzing(true);
    try {
      const formData = new FormData();
      if (userPrompt.trim()) formData.append('user_prompt', userPrompt.trim());
      const { data } = await axios.post(`${API}/products/${draftProduct.id}/regenerate-ai-images`, formData, {
        withCredentials: true,
        headers: { ...getHeaders(), 'Content-Type': 'multipart/form-data' },
        timeout: 240000
      });
      setDraftProduct(prev => ({ ...prev, images: data.images }));
      if (data.ai_generation_error) {
        toast.warning(`AI error: ${data.ai_generation_error.slice(0, 120)}`);
      } else {
        toast.success('Immagini AI rigenerate!');
      }
    } catch (e) {
      toast.error('Errore rigenerazione immagini AI');
    } finally {
      setAnalyzing(false);
    }
  };

  const updateDraftField = (field, value) => {
    setDraftProduct(prev => ({ ...prev, [field]: value }));
  };

  const addVariant = () => {
    const variants = [...(draftProduct.variants || []), { type: '', options: [''] }];
    updateDraftField('variants', variants);
  };

  const updateVariant = (idx, field, value) => {
    const variants = [...(draftProduct.variants || [])];
    if (field === 'options') {
      variants[idx].options = value.split(',').map(s => s.trim()).filter(Boolean);
    } else {
      variants[idx][field] = value;
    }
    updateDraftField('variants', variants);
  };

  const removeVariant = (idx) => {
    const variants = (draftProduct.variants || []).filter((_, i) => i !== idx);
    updateDraftField('variants', variants);
  };

  const advanceFromQueue = () => {
    if (draftQueue.length === 0) {
      setDraftProduct(null);
      fetchProducts();
      return false;
    }
    const [next, ...rest] = draftQueue;
    setDraftQueue(rest);
    setDraftProduct(next);
    return true;
  };

  const publishProduct = async () => {
    if (!draftProduct) return;
    try {
      await axios.put(`${API}/products/${draftProduct.id}`, {
        title: draftProduct.title,
        description: draftProduct.description,
        price: parseFloat(draftProduct.price) || 0,
        original_price: draftProduct.original_price ? parseFloat(draftProduct.original_price) : null,
        category: draftProduct.category,
        variants: draftProduct.variants,
        tags: draftProduct.tags || [],
        status: 'published'
      }, { withCredentials: true, headers: getHeaders() });
      toast.success('Prodotto pubblicato!');
      const advanced = advanceFromQueue();
      if (!advanced) {
        setActiveTab('products');
      }
    } catch (e) {
      toast.error('Errore pubblicazione');
    }
  };

  const saveDraft = async () => {
    if (!draftProduct) return;
    try {
      await axios.put(`${API}/products/${draftProduct.id}`, {
        title: draftProduct.title,
        description: draftProduct.description,
        price: parseFloat(draftProduct.price) || 0,
        original_price: draftProduct.original_price ? parseFloat(draftProduct.original_price) : null,
        category: draftProduct.category,
        variants: draftProduct.variants,
        tags: draftProduct.tags || [],
        status: 'draft'
      }, { withCredentials: true });
      toast.success('Bozza salvata!');
    } catch (e) {
      toast.error('Errore salvataggio');
    }
  };

  const loadPreview = async () => {
    if (!draftProduct?.id) return;
    try {
      // Get product with card image
      const { data: prod } = await axios.get(`${API}/products/${draftProduct.id}`);
      const cardImg = prod.images?.find(i => i.type === 'card') || prod.images?.[0];
      if (cardImg) {
        const path = cardImg.storage_path || cardImg.external_url;
        if (cardImg.external_url) {
          setPreviewImgSrc(cardImg.external_url);
        } else if (path) {
          const res = await axios.get(`${API}/files/${path}`, { responseType: 'blob' });
          setPreviewImgSrc(URL.createObjectURL(res.data));
        }
      }
      setShowPreview(true);
    } catch (e) {
      toast.error('Errore caricamento anteprima');
    }
  };

  const togglePublish = async (product) => {
    const newStatus = product.status === 'published' ? 'draft' : 'published';
    try {
      await axios.put(`${API}/products/${product.id}`, { status: newStatus }, { withCredentials: true });
      toast.success(newStatus === 'published' ? 'Pubblicato!' : 'Rimosso dalla vetrina');
      fetchProducts();
    } catch (e) {
      toast.error('Errore');
    }
  };

  const deleteProduct = async (productId) => {
    if (!window.confirm('Sei sicuro di eliminare questo prodotto?')) return;
    try {
      await axios.delete(`${API}/products/${productId}`, { withCredentials: true });
      toast.success('Prodotto eliminato');
      fetchProducts();
    } catch (e) {
      toast.error('Errore eliminazione');
    }
  };

  const addImageToProduct = async (productId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    try {
      await axios.post(`${API}/products/${productId}/add-image`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Immagine aggiunta!');
      fetchProducts();
    } catch (e) {
      toast.error('Errore aggiunta immagine');
    }
  };

  return (
    <div data-testid="admin-page" className="pb-safe px-4 pt-4">
      <h1 className="text-2xl font-semibold text-[#2A2624] mb-4" style={{ fontFamily: 'Outfit' }}>
        Gestione Prodotti
      </h1>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="w-full bg-[#F0EBE1] rounded-xl mb-4">
          <TabsTrigger data-testid="tab-upload" value="upload" className="flex-1 rounded-lg data-[state=active]:bg-[#C25934] data-[state=active]:text-white">
            <Camera size={16} className="mr-1.5" /> Carica
          </TabsTrigger>
          <TabsTrigger data-testid="tab-products" value="products" className="flex-1 rounded-lg data-[state=active]:bg-[#C25934] data-[state=active]:text-white">
            <ImageIcon size={16} className="mr-1.5" /> Prodotti ({products.length})
          </TabsTrigger>
        </TabsList>

        {/* UPLOAD TAB */}
        <TabsContent value="upload" className="space-y-4">
          {/* AI Plan badge */}
          {aiPlanStatus && (
            <div
              data-testid="ai-plan-badge"
              className={`rounded-xl p-2.5 text-xs flex items-center justify-between gap-2 ${aiPlanStatus.active ? 'bg-[#4A6B53]/5 border border-[#4A6B53]/20 text-[#4A6B53]' : 'bg-amber-50 border border-amber-300 text-amber-800'}`}
            >
              <div className="flex items-center gap-2">
                <span className="text-base">✨</span>
                <span>
                  {aiPlanStatus.active
                    ? <><b>Piano AI attivo</b> — {aiPlanStatus.plan.used}/{aiPlanStatus.plan.included} · scade {new Date(aiPlanStatus.plan.expires_at).toLocaleDateString('it-IT')}</>
                    : <><b>Piano AI non attivo</b> — le foto verranno caricate senza generazione AI</>
                  }
                </span>
              </div>
              {!aiPlanStatus.active && (
                <button
                  data-testid="go-ai-pricing-btn"
                  onClick={() => navigate('/piani-ai')}
                  className="flex-shrink-0 bg-amber-600 hover:bg-amber-700 text-white text-xs font-semibold px-3 py-1.5 rounded-full"
                >
                  Attiva →
                </button>
              )}
            </div>
          )}

          {/* Today's Flyer Info */}
          {todayFlyer && (
            <div className="bg-[#C25934]/5 rounded-xl p-3 flex items-center justify-between">
              <div>
                <p className="text-xs font-bold text-[#C25934]">Volantino di Oggi</p>
                <p className="text-[10px] text-[#877E78]">{todayFlyer.product_count || 0} prodotti caricati</p>
              </div>
              <button
                data-testid="share-flyer-btn"
                onClick={() => navigate(`/volantino/${todayFlyer.id}`)}
                className="text-xs font-medium text-[#C25934] px-3 py-1.5 rounded-lg bg-[#C25934]/10 hover:bg-[#C25934]/20"
              >
                Condividi
              </button>
            </div>
          )}

          {/* Recovery banner: invita a riprendere prodotti dai vecchi volantini */}
          <button
            data-testid="open-recovery-btn"
            onClick={() => setRecoveryOpen(true)}
            className="w-full flex items-center gap-3 bg-gradient-to-r from-[#4A6B53]/10 to-[#C25934]/10 hover:from-[#4A6B53]/20 hover:to-[#C25934]/20 rounded-xl p-3 text-left border border-[#4A6B53]/20"
          >
            <div className="w-10 h-10 rounded-lg bg-[#4A6B53] text-white flex items-center justify-center">
              🔄
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-[#2A2624]" style={{ fontFamily: 'Outfit' }}>
                Recupera prodotti dai vecchi volantini
              </p>
              <p className="text-[11px] text-[#5C554F]">
                Seleziona quelli che hai ancora a magazzino e rimettili nel volantino di oggi
              </p>
            </div>
            <span className="text-[#4A6B53] font-bold">→</span>
          </button>

          {!draftProduct ? (
            <div className="space-y-4">
              {/* Mode Toggle — temporarily hidden: only "photo" mode used. Batch/Barcode preserved below for future re-enable */}
              {false && (
              <div className="flex gap-2">
                <button
                  data-testid="mode-photo"
                  onClick={() => setUploadMode('photo')}
                  className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-medium transition-all ${
                    uploadMode === 'photo' ? 'bg-[#C25934] text-white' : 'bg-[#F0EBE1] text-[#5C554F]'
                  }`}
                >
                  <Camera size={16} /> Foto
                </button>
                <button
                  data-testid="mode-batch"
                  onClick={() => setUploadMode('batch')}
                  className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-medium transition-all ${
                    uploadMode === 'batch' ? 'bg-[#C25934] text-white' : 'bg-[#F0EBE1] text-[#5C554F]'
                  }`}
                >
                  <Upload size={16} /> Multiplo
                </button>
                <button
                  data-testid="mode-barcode"
                  onClick={() => setUploadMode('barcode')}
                  className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-medium transition-all ${
                    uploadMode === 'barcode' ? 'bg-[#C25934] text-white' : 'bg-[#F0EBE1] text-[#5C554F]'
                  }`}
                >
                  <Barcode size={16} /> Barcode
                </button>
              </div>
              )}

              {uploadMode === 'photo' ? (
                /* Photo Upload Area — Guided multi-shot */
                <div
                  data-testid="upload-area"
                  onClick={startShotFlow}
                  className="border-2 border-dashed border-[#C25934]/30 bg-[#F0EBE1]/50 rounded-3xl p-8 text-center cursor-pointer transition-all duration-300 hover:border-[#C25934]/60 hover:bg-[#F0EBE1] active:scale-[0.98]"
                >
                  {analyzing ? (
                    <div className="space-y-3">
                      <Loader2 size={48} className="mx-auto text-[#C25934] animate-spin" />
                      <p className="text-[#C25934] font-medium">
                        {batchProgress
                          ? `Elaborazione prodotto ${batchProgress.current}/${batchProgress.total}...`
                          : 'Elaborazione AI in corso...'}
                      </p>
                      <p className="text-[#877E78] text-sm">
                        {batchProgress
                          ? 'Ogni prodotto richiede ~10-15s. Resta sulla pagina.'
                          : 'Estrazione dati e generazione immagini'}
                      </p>
                      {batchProgress && (
                        <div className="w-full max-w-xs mx-auto h-1.5 bg-[#F0EBE1] rounded-full overflow-hidden">
                          <div
                            className="h-full bg-[#C25934] transition-all"
                            style={{ width: `${(batchProgress.current / batchProgress.total) * 100}%` }}
                          />
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="space-y-3">
                      <div className="w-20 h-20 bg-[#C25934]/10 rounded-full flex items-center justify-center mx-auto">
                        <Camera size={36} className="text-[#C25934]" />
                      </div>
                      <p className="text-lg font-medium text-[#2A2624]" style={{ fontFamily: 'Outfit' }}>
                        Inizia scatto guidato
                      </p>
                      <p className="text-sm text-[#877E78]">
                        Scatta fino a 6 foto con anteprima. L'AI estrae dati e genera immagini pulite.
                      </p>
                    </div>
                  )}
                </div>
              ) : uploadMode === 'barcode' ? (
                /* Barcode Scanner */
                <div className="border-2 border-dashed border-[#C25934]/30 bg-[#F0EBE1]/50 rounded-3xl p-6 space-y-4">
                  <div className="text-center">
                    <div className="w-16 h-16 bg-[#C25934]/10 rounded-full flex items-center justify-center mx-auto mb-2">
                      <ScanLine size={32} className="text-[#C25934]" />
                    </div>
                    <p className="text-lg font-medium text-[#2A2624]" style={{ fontFamily: 'Outfit' }}>
                      Inserisci Codice a Barre
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <Input
                      data-testid="barcode-input"
                      value={barcodeInput}
                      onChange={e => setBarcodeInput(e.target.value)}
                      placeholder="Es: 8001234567890"
                      className="h-12 rounded-xl border-[#E8E3D9] flex-1"
                      onKeyDown={e => e.key === 'Enter' && handleBarcodeLookup()}
                    />
                    <Button
                      data-testid="barcode-search-btn"
                      onClick={handleBarcodeLookup}
                      disabled={barcodeLoading}
                      className="h-12 px-4 rounded-xl bg-[#C25934] hover:bg-[#A64B2B] text-white"
                    >
                      {barcodeLoading ? <Loader2 size={18} className="animate-spin" /> : 'Cerca'}
                    </Button>
                  </div>
                  <p className="text-xs text-[#877E78] text-center">
                    Se non trovato, passa alla modalità foto
                  </p>
                </div>
              ) : uploadMode === 'batch' ? (
                /* Batch Upload */
                <div
                  data-testid="batch-upload-area"
                  onClick={() => document.getElementById('batch-input')?.click()}
                  className="border-2 border-dashed border-[#C25934]/30 bg-[#F0EBE1]/50 rounded-3xl p-8 text-center cursor-pointer transition-all duration-300 hover:border-[#C25934]/60 active:scale-[0.98]"
                >
                  <input
                    id="batch-input"
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={handleBatchUpload}
                    className="hidden"
                    data-testid="batch-file-input"
                  />
                  {analyzing ? (
                    <div className="space-y-3">
                      <Loader2 size={48} className="mx-auto text-[#C25934] animate-spin" />
                      <p className="text-[#C25934] font-medium">Analisi AI in corso...</p>
                      <p className="text-[#877E78] text-sm">Elaborazione foto multiple</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      <div className="w-20 h-20 bg-[#C25934]/10 rounded-full flex items-center justify-center mx-auto">
                        <Upload size={36} className="text-[#C25934]" />
                      </div>
                      <p className="text-lg font-medium text-[#2A2624]" style={{ fontFamily: 'Outfit' }}>
                        Carica Foto Multiple
                      </p>
                      <p className="text-sm text-[#877E78]">
                        Seleziona fino a 10 foto. L'AI le analizzerà tutte automaticamente.
                      </p>
                    </div>
                  )}
                </div>
              ) : null}
            </div>
          ) : (
            /* DRAFT EDITOR */
            <div className="space-y-4">
              <div className="flex items-center justify-between gap-2">
                <div>
                  <h2 className="text-lg font-medium text-[#2A2624]" style={{ fontFamily: 'Outfit' }}>
                    Modifica prodotto
                  </h2>
                  {draftQueue.length > 0 && (
                    <p className="text-[11px] text-[#C25934] font-medium">
                      📋 {draftQueue.length} {draftQueue.length === 1 ? 'altro prodotto' : 'altri prodotti'} in coda — pubblica o salta per passare al prossimo
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-1">
                  {draftQueue.length > 0 && (
                    <Button
                      data-testid="skip-draft-btn"
                      onClick={() => { advanceFromQueue(); toast.message('Saltato. Resta come bozza.'); }}
                      variant="outline"
                      size="sm"
                      className="h-9 px-3 rounded-full text-xs border-[#E8E3D9] text-[#5C554F]"
                    >
                      Salta →
                    </Button>
                  )}
                  <button
                    data-testid="close-draft"
                    onClick={() => {
                      if (draftQueue.length > 0) {
                        if (window.confirm(`Vuoi davvero chiudere? Ci sono ${draftQueue.length} prodotti in coda non ancora rivisti (rimarranno come bozza).`)) {
                          setDraftQueue([]);
                          setDraftProduct(null);
                          fetchProducts();
                          setActiveTab('products');
                        }
                      } else {
                        setDraftProduct(null);
                      }
                    }}
                    className="p-2 rounded-full hover:bg-[#F0EBE1]"
                  >
                    <X size={20} className="text-[#877E78]" />
                  </button>
                </div>
              </div>

              {/* AI Raw Text */}
              {draftProduct.ai_raw && (
                <div className="bg-[#F0EBE1]/50 rounded-xl p-3 text-xs text-[#877E78]">
                  <span className="font-bold text-[#A64B2B]">Testo AI: </span>{draftProduct.ai_raw}
                </div>
              )}

              {/* Photo Gallery - categorized */}
              {draftProduct.images?.length > 0 && (() => {
                const aiMain = draftProduct.images.filter(i => i.type === 'ai_main');
                const aiVariants = draftProduct.images.filter(i => i.type === 'ai_variants');
                const originals = draftProduct.images.filter(i => i.type === 'original');
                const cleans = draftProduct.images.filter(i => i.type === 'clean');
                const mkDeleteFor = (img) => async () => {
                  if (!draftProduct.id) return;
                  const idx = draftProduct.images.findIndex(x => x === img);
                  if (idx < 0) return;
                  try {
                    await axios.delete(`${API}/products/${draftProduct.id}/images/${idx}`, { withCredentials: true, headers: getHeaders() });
                    const newImages = [...draftProduct.images];
                    newImages.splice(idx, 1);
                    setDraftProduct(prev => ({ ...prev, images: newImages }));
                    toast.success('Foto eliminata');
                  } catch { toast.error('Errore eliminazione foto'); }
                };
                const renderSection = (label, items, tone) => items.length > 0 && (
                  <div>
                    <div className="flex items-center justify-between">
                      <label className={`text-xs font-bold uppercase tracking-wider ${tone}`}>{label} ({items.length})</label>
                    </div>
                    <div className="flex gap-2 overflow-x-auto no-scrollbar mt-1.5 pb-1">
                      {items.map((img, i) => (
                        <DraftImageThumb key={`${label}-${i}`} img={img} onDelete={mkDeleteFor(img)} />
                      ))}
                    </div>
                  </div>
                );
                return (
                  <div className="space-y-3">
                    {renderSection('🤖 Foto AI principale', aiMain, 'text-[#4A6B53]')}
                    {renderSection('🌈 Varianti colore AI', aiVariants, 'text-[#8B5CF6]')}
                    {renderSection('📷 Scatti originali', [...cleans, ...originals], 'text-[#877E78]')}
                    <Button
                      data-testid="regenerate-ai-btn"
                      onClick={regenerateAiImages}
                      disabled={analyzing}
                      variant="outline"
                      className="w-full h-10 rounded-full border-[#C25934]/40 text-[#C25934] hover:bg-[#C25934]/5 text-sm"
                    >
                      {analyzing ? (
                        <><Loader2 size={14} className="mr-1.5 animate-spin" /> Rigenerazione...</>
                      ) : (
                        <>🔄 Rigenera immagini AI {aiMain.length === 0 ? '(genera)' : ''}</>
                      )}
                    </Button>
                    <ImageSearchButton
                      productId={draftProduct.id}
                      defaultQuery={draftProduct.title || ''}
                    />
                  </div>
                );
              })()}

              <div className="space-y-3">
                <div>
                  <label className="text-sm font-medium text-[#5C554F]">Titolo</label>
                  <Input
                    data-testid="edit-title"
                    value={draftProduct.title || ''}
                    onChange={e => updateDraftField('title', e.target.value)}
                    className="h-12 rounded-xl border-[#E8E3D9] mt-1"
                  />
                </div>

                <div>
                  <label className="text-sm font-medium text-[#5C554F]">Descrizione</label>
                  <textarea
                    data-testid="edit-description"
                    value={draftProduct.description || ''}
                    onChange={e => updateDraftField('description', e.target.value)}
                    rows={3}
                    className="w-full mt-1 px-3 py-2 rounded-xl border border-[#E8E3D9] bg-white text-sm resize-none outline-none focus:ring-2 focus:ring-[#C25934]"
                  />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-sm font-medium text-[#5C554F]">Prezzo (&euro;)</label>
                    <Input
                      data-testid="edit-price"
                      type="number"
                      step="0.01"
                      value={draftProduct.price || ''}
                      onChange={e => updateDraftField('price', e.target.value)}
                      className="h-12 rounded-xl border-[#E8E3D9] mt-1"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-[#5C554F]">Prezzo Originale</label>
                    <Input
                      data-testid="edit-original-price"
                      type="number"
                      step="0.01"
                      value={draftProduct.original_price || ''}
                      onChange={e => updateDraftField('original_price', e.target.value)}
                      className="h-12 rounded-xl border-[#E8E3D9] mt-1"
                    />
                  </div>
                </div>

                <div>
                  <label className="text-sm font-medium text-[#5C554F]">Categoria</label>
                  <Select
                    value={draftProduct.category || 'Altro'}
                    onValueChange={val => updateDraftField('category', val)}
                  >
                    <SelectTrigger data-testid="edit-category" className="h-12 rounded-xl border-[#E8E3D9] mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {(shopCategories.length > 0 ? shopCategories : ['Novità', 'In Offerta', 'Più Venduti', 'Altro']).map(c => (
                        <SelectItem key={c} value={c}>{c}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="text-sm font-medium text-[#5C554F]">Tag (per sezioni home automatiche)</label>
                  <input
                    data-testid="edit-tags"
                    type="text"
                    value={(draftProduct.tags || []).join(', ')}
                    onChange={e => updateDraftField('tags', e.target.value.split(',').map(t => t.trim().toLowerCase()).filter(Boolean))}
                    placeholder="es: biancheria, letto, cotone"
                    className="w-full h-12 px-4 rounded-xl border border-[#E8E3D9] bg-white mt-1"
                  />
                  <p className="text-[10px] text-[#877E78] mt-1">Separa i tag con virgole. Creano sezioni auto nella home del tuo negozio.</p>
                </div>

                {/* Variants */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-sm font-bold text-[#2A2624] uppercase tracking-wider">Varianti</label>
                    <button
                      data-testid="add-variant"
                      onClick={addVariant}
                      className="flex items-center gap-1 text-xs text-[#C25934] font-medium hover:text-[#A64B2B]"
                    >
                      <Plus size={14} /> Aggiungi
                    </button>
                  </div>
                  {(draftProduct.variants || []).map((v, idx) => (
                    <div key={idx} className="flex gap-2 items-start mb-2">
                      <Input
                        data-testid={`variant-type-${idx}`}
                        placeholder="Tipo (es: taglia)"
                        value={v.type}
                        onChange={e => updateVariant(idx, 'type', e.target.value)}
                        className="h-10 rounded-lg border-[#E8E3D9] text-sm flex-1"
                      />
                      <Input
                        data-testid={`variant-options-${idx}`}
                        placeholder="Opzioni (es: S, M, L)"
                        value={(v.options || []).join(', ')}
                        onChange={e => updateVariant(idx, 'options', e.target.value)}
                        className="h-10 rounded-lg border-[#E8E3D9] text-sm flex-[2]"
                      />
                      <button
                        onClick={() => removeVariant(idx)}
                        className="p-2 text-red-500 hover:bg-red-50 rounded-lg"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              {/* Actions */}
              <div className="space-y-2 pt-2">
                {/* Preview button */}
                <Button
                  data-testid="preview-btn"
                  onClick={loadPreview}
                  variant="outline"
                  className="w-full h-12 rounded-full border-[#D98324] text-[#D98324] hover:bg-[#D98324]/5 font-medium"
                >
                  <Eye size={18} className="mr-1.5" /> Anteprima Scheda Prodotto
                </Button>
                <div className="flex gap-3">
                  <Button
                    data-testid="save-draft-btn"
                    onClick={saveDraft}
                    variant="outline"
                    className="flex-1 h-12 rounded-full border-[#E8E3D9] text-[#5C554F]"
                  >
                    Salva Bozza
                  </Button>
                  <Button
                    data-testid="publish-btn"
                    onClick={publishProduct}
                    className="flex-1 h-12 rounded-full bg-[#C25934] hover:bg-[#A64B2B] text-white font-semibold"
                  >
                    <Check size={18} className="mr-1.5" /> Pubblica
                  </Button>
                </div>
              </div>

              {/* Preview Modal */}
              {showPreview && (
                <div className="fixed inset-0 z-[9999] bg-black/70 flex items-center justify-center p-4" onClick={() => setShowPreview(false)}>
                  <div className="bg-white rounded-2xl max-w-sm w-full overflow-hidden" onClick={e => e.stopPropagation()}>
                    <div className="p-3 border-b border-[#E8E3D9] flex items-center justify-between">
                      <h3 className="font-medium text-[#2A2624] text-sm" style={{ fontFamily: 'Outfit' }}>Anteprima Scheda Prodotto</h3>
                      <button onClick={() => setShowPreview(false)} className="p-1 rounded-full hover:bg-[#F0EBE1]">
                        <X size={18} className="text-[#877E78]" />
                      </button>
                    </div>
                    {previewImgSrc ? (
                      <img src={previewImgSrc} alt="Anteprima" className="w-full" />
                    ) : (
                      <div className="h-64 flex items-center justify-center text-[#877E78]">
                        <Loader2 size={24} className="animate-spin" />
                      </div>
                    )}
                    <div className="p-3 flex gap-2">
                      <Button onClick={() => setShowPreview(false)} variant="outline" className="flex-1 h-10 rounded-full border-[#E8E3D9] text-sm">
                        Modifica
                      </Button>
                      <Button onClick={() => { setShowPreview(false); publishProduct(); }} className="flex-1 h-10 rounded-full bg-[#C25934] hover:bg-[#A64B2B] text-white text-sm">
                        Pubblica
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </TabsContent>

        {/* PRODUCTS TAB */}
        <TabsContent value="products" className="space-y-3">
          {/* Search + filters bar */}
          <div className="bg-white rounded-2xl border border-[#E8E3D9] p-3 space-y-2">
            <div className="relative">
              <input
                data-testid="products-search-input"
                type="search"
                placeholder="Cerca per titolo, descrizione, categoria, tag..."
                value={productsSearch}
                onChange={e => setProductsSearch(e.target.value)}
                className="w-full h-11 pl-9 pr-9 rounded-xl border border-[#E8E3D9] bg-[#FDFBF7] text-sm outline-none focus:ring-2 focus:ring-[#C25934] focus:border-[#C25934]"
              />
              <svg className="absolute left-3 top-1/2 -translate-y-1/2 text-[#877E78]" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="11" cy="11" r="8" />
                <path d="m21 21-4.3-4.3" />
              </svg>
              {productsSearch && (
                <button
                  data-testid="clear-search-btn"
                  onClick={() => setProductsSearch('')}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded-full hover:bg-[#F0EBE1] text-[#877E78]"
                >
                  <X size={14} />
                </button>
              )}
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              {[
                { key: 'all', label: 'Tutti' },
                { key: 'published', label: 'Pubblicati' },
                { key: 'draft', label: 'Bozze' },
              ].map(f => (
                <button
                  key={f.key}
                  data-testid={`filter-${f.key}`}
                  onClick={() => setProductsStatusFilter(f.key)}
                  className={`text-xs font-medium px-3 py-1.5 rounded-full border transition-colors ${
                    productsStatusFilter === f.key
                      ? 'bg-[#C25934] text-white border-[#C25934]'
                      : 'bg-white text-[#5C554F] border-[#E8E3D9] hover:bg-[#F0EBE1]'
                  }`}
                >
                  {f.label}
                </button>
              ))}
              <span className="text-[11px] text-[#877E78] ml-auto">{products.length} risultati</span>
            </div>
          </div>

          <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
            <h3 className="text-sm font-bold text-[#5C554F] uppercase tracking-wider">Archivio prodotti</h3>
            <div className="flex items-center gap-2">
              <a
                data-testid="view-as-customer-btn"
                href={shopSlug ? `/negozio/${shopSlug}` : `/catalogo`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1.5 h-8 px-3 rounded-full border border-[#4A6B53]/40 bg-[#4A6B53]/5 text-[#4A6B53] hover:bg-[#4A6B53]/10 text-xs font-medium"
              >
                <Eye size={13} /> Vedi come cliente ↗
              </a>
              <Button
                data-testid="open-tag-order-btn"
                onClick={() => setTagOrderOpen(true)}
                variant="outline"
                size="sm"
                className="h-8 rounded-full border-[#C25934]/40 text-[#C25934] text-xs"
              >
                🏷️ Ordina sezioni home
            </Button>
            </div>
          </div>
          {loadingProducts ? (
            <div className="space-y-3">
              {[1,2,3].map(i => (
                <div key={i} className="h-20 skeleton-shimmer rounded-xl" />
              ))}
            </div>
          ) : products.length === 0 ? (
            <div className="text-center py-12">
              <ImageIcon size={40} className="mx-auto text-[#877E78] mb-3" />
              <p className="text-[#877E78]">{productsSearch ? `Nessun prodotto trovato per "${productsSearch}"` : 'Nessun prodotto ancora'}</p>
            </div>
          ) : (
            products.map(p => (
              <ProductAdminCard
                key={p.id}
                product={p}
                shop={shop}
                onTogglePublish={togglePublish}
                onDelete={deleteProduct}
                onAddImage={addImageToProduct}
                onPreview={openProductPreview}
                onEdit={openProductEdit}
                onAddToFlyer={addToTodayFlyer}
                onEditStock={setStockEditProduct}
              />
            ))
          )}
        </TabsContent>
      </Tabs>

      {/* Hidden input for guided single-shot capture */}
      <input
        ref={shotInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        onChange={onShotTaken}
        className="hidden"
        data-testid="shot-input"
      />
      {/* Hidden input for multi-photo upload from gallery (within Scatto Guidato modal) */}
      <input
        ref={multiUploadRef}
        type="file"
        accept="image/*"
        multiple
        onChange={onMultiPhotosUpload}
        className="hidden"
        data-testid="multi-upload-input"
      />

      {/* GUIDED SHOT FLOW MODAL */}
      {shotFlowActive && (
        <div className="fixed inset-0 z-[9999] bg-black/85 flex items-end sm:items-center justify-center p-0 sm:p-4" data-testid="shot-flow-modal">
          <div className="bg-white w-full sm:max-w-md sm:rounded-2xl rounded-t-3xl overflow-hidden flex flex-col max-h-[95vh]">
            {/* Header */}
            <div className="px-4 py-3 border-b border-[#E8E3D9] flex items-center justify-between">
              <div>
                <h3 className="font-semibold text-[#2A2624]" style={{ fontFamily: 'Outfit' }}>
                  {shotPhase === 'review' ? 'Controlla lo scatto' : shotPhase === 'validate' ? 'Pronto per l\'AI' : 'Scatto guidato'}
                </h3>
                <p className="text-[11px] text-[#877E78]">{shots.length}/6 foto confermate</p>
              </div>
              <button data-testid="shot-cancel-btn" onClick={cancelShotFlow} className="p-2 rounded-full hover:bg-[#F0EBE1]">
                <X size={20} className="text-[#877E78]" />
              </button>
            </div>

            {/* BODY */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {shotPhase === 'review' && pendingShot && (
                <div className="space-y-3">
                  <div className="rounded-2xl overflow-hidden bg-black flex items-center justify-center aspect-square">
                    <img src={pendingShot.preview} alt="scatto" className="w-full h-full object-cover" />
                  </div>
                  <p className="text-center text-sm text-[#5C554F]">
                    Scatto {shots.length + 1} · ritaglio quadrato. Vuoi usarlo?
                  </p>
                </div>
              )}

              {shotPhase === 'capture' && (
                <div className="space-y-3">
                  {shots.length > 0 && (
                    <div>
                      <label className="text-[10px] font-bold text-[#877E78] uppercase tracking-wider">
                        Scatti confermati ({shots.length}/6)
                      </label>
                      <div className="grid grid-cols-6 gap-1.5 mt-1.5">
                        {shots.map((s, idx) => (
                          <div key={idx} className="relative aspect-square rounded-lg overflow-hidden bg-[#F0EBE1] border border-[#E8E3D9]">
                            <img src={s.preview} alt={`s-${idx}`} className="w-full h-full object-cover" />
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  <div className="bg-[#F0EBE1]/50 rounded-2xl p-6 text-center border-2 border-dashed border-[#C25934]/30">
                    <Camera size={40} className="mx-auto text-[#C25934] mb-2" />
                    <p className="font-medium text-[#2A2624]" style={{ fontFamily: 'Outfit' }}>
                      {shots.length === 0 ? 'Scatta la prima foto' : `Aggiungi un'altra foto? (${shots.length}/6)`}
                    </p>
                    <p className="text-xs text-[#877E78] mt-1">
                      {shots.length === 0
                        ? 'Basta anche 1 foto · max 6'
                        : 'Puoi terminare ora o scattare ancora'}
                    </p>
                    <Button
                      data-testid="open-camera-btn"
                      onClick={() => shotInputRef.current?.click()}
                      className="mt-4 h-11 px-6 rounded-full bg-[#C25934] hover:bg-[#A64B2B] text-white font-medium"
                    >
                      <Camera size={16} className="mr-2" />
                      {shots.length === 0 ? 'Apri fotocamera' : 'Scatta un\'altra foto'}
                    </Button>
                    <div className="flex items-center gap-2 my-3 text-[10px] text-[#877E78] uppercase tracking-wider">
                      <div className="flex-1 h-px bg-[#E8E3D9]" />
                      oppure
                      <div className="flex-1 h-px bg-[#E8E3D9]" />
                    </div>
                    <Button
                      data-testid="multi-upload-btn"
                      type="button"
                      variant="outline"
                      onClick={() => multiUploadRef.current?.click()}
                      className="h-11 px-6 rounded-full border-[#C25934]/40 text-[#C25934] hover:bg-[#C25934]/5 font-medium"
                    >
                      <Upload size={16} className="mr-2" />
                      Carica foto dalla galleria
                    </Button>
                    <p className="text-[10px] text-[#877E78] mt-2">
                      Puoi caricare fino a {Math.max(0, 6 - shots.length)} {6 - shots.length === 1 ? 'foto' : 'foto'} in una volta sola
                    </p>
                  </div>
                </div>
              )}

              {shotPhase === 'validate' && (
                <div className="space-y-4">
                  <div>
                    <label className="text-xs font-bold text-[#5C554F] uppercase tracking-wider">Scatti ({shots.length})</label>
                    <div className="grid grid-cols-3 gap-2 mt-2">
                      {shots.map((s, idx) => (
                        <div key={idx} className="relative aspect-square rounded-xl overflow-hidden bg-[#F0EBE1] border border-[#E8E3D9]">
                          <img src={s.preview} alt={`scatto-${idx}`} className="w-full h-full object-cover" />
                          <button
                            data-testid={`remove-shot-${idx}`}
                            onClick={() => removeShot(idx)}
                            className="absolute top-1 right-1 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600"
                          >
                            <X size={12} />
                          </button>
                        </div>
                      ))}
                      {shots.length < 6 && (
                        <button
                          data-testid="add-more-shots"
                          onClick={() => { setShotPhase('capture'); setTimeout(() => shotInputRef.current?.click(), 50); }}
                          className="aspect-square rounded-xl border-2 border-dashed border-[#C25934]/40 flex items-center justify-center text-[#C25934] hover:bg-[#C25934]/5"
                        >
                          <Plus size={24} />
                        </button>
                      )}
                    </div>
                  </div>

                  <div>
                    <label className="text-xs font-bold text-[#5C554F] uppercase tracking-wider">
                      Istruzioni specifiche AI (opzionale)
                    </label>
                    <textarea
                      data-testid="user-prompt-input"
                      value={userPrompt}
                      onChange={e => setUserPrompt(e.target.value)}
                      rows={3}
                      placeholder="Es: sono tende 140x290, evidenzia il tessuto nobile, prezzo €6,00 al paio..."
                      className="w-full mt-1 px-3 py-2 rounded-xl border border-[#E8E3D9] bg-white text-sm resize-none outline-none focus:ring-2 focus:ring-[#C25934]"
                    />
                    <p className="text-[10px] text-[#877E78] mt-1">L'AI userà queste info per migliorare titolo, descrizione e le immagini generate.</p>
                  </div>

                  {/* Toggle: stesso prodotto vs prodotti diversi */}
                  {shots.length >= 2 && (
                    <div data-testid="multi-product-mode-section" className="rounded-xl border border-[#E8E3D9] overflow-hidden">
                      <button
                        type="button"
                        data-testid="multi-product-mode-same"
                        onClick={() => setMultiProductMode(false)}
                        className={`w-full flex items-start gap-3 p-3 text-left transition ${!multiProductMode ? 'bg-[#C25934]/10 border-l-4 border-[#C25934]' : 'bg-white hover:bg-[#F0EBE1]/40'}`}
                      >
                        <div className={`mt-0.5 w-4 h-4 rounded-full border-2 flex-shrink-0 flex items-center justify-center ${!multiProductMode ? 'border-[#C25934] bg-[#C25934]' : 'border-[#E8E3D9]'}`}>
                          {!multiProductMode && <div className="w-1.5 h-1.5 bg-white rounded-full" />}
                        </div>
                        <div className="flex-1">
                          <p className="text-sm font-semibold text-[#2A2624]">Stesso prodotto</p>
                          <p className="text-[11px] text-[#877E78] leading-snug">
                            Tutte le foto sono dello stesso articolo (es. davanti, retro, etichetta, varianti colore). L'AI le combina in <b>1 prodotto</b>.
                          </p>
                        </div>
                      </button>
                      <button
                        type="button"
                        data-testid="multi-product-mode-diff"
                        onClick={() => setMultiProductMode(true)}
                        className={`w-full flex items-start gap-3 p-3 text-left border-t border-[#E8E3D9] transition ${multiProductMode ? 'bg-[#C25934]/10 border-l-4 border-[#C25934]' : 'bg-white hover:bg-[#F0EBE1]/40'}`}
                      >
                        <div className={`mt-0.5 w-4 h-4 rounded-full border-2 flex-shrink-0 flex items-center justify-center ${multiProductMode ? 'border-[#C25934] bg-[#C25934]' : 'border-[#E8E3D9]'}`}>
                          {multiProductMode && <div className="w-1.5 h-1.5 bg-white rounded-full" />}
                        </div>
                        <div className="flex-1">
                          <p className="text-sm font-semibold text-[#2A2624]">Prodotti diversi</p>
                          <p className="text-[11px] text-[#877E78] leading-snug">
                            Ogni foto è un articolo distinto. L'AI processa <b>{shots.length} prodotti</b> separati con lo stesso prompt.
                          </p>
                        </div>
                      </button>
                    </div>
                  )}

                  <div className="bg-[#C25934]/5 border border-[#C25934]/20 rounded-xl p-3 text-xs text-[#5C554F]">
                    🤖 <b>Cosa farà l'AI:</b> {multiProductMode && shots.length >= 2
                      ? `creerà ${shots.length} prodotti separati, uno per foto. Ogni elaborazione richiede ~10-15s, quindi attendi qualche minuto.`
                      : 'estrae titolo/prezzo/varianti dalle foto e genera 1 foto e-commerce pulita + 1 griglia varianti colore.'}
                  </div>
                </div>
              )}
            </div>

            {/* FOOTER — action buttons per phase */}
            <div className="border-t border-[#E8E3D9] p-3">
              {shotPhase === 'review' && (
                <div className="space-y-2">
                  <div className="grid grid-cols-3 gap-2">
                    <Button data-testid="shot-use-btn" onClick={confirmPendingShot} className="h-11 rounded-full bg-[#4A6B53] hover:bg-[#3a5643] text-white font-medium">
                      <Check size={16} className="mr-1.5" /> Usa
                    </Button>
                    <Button data-testid="shot-retry-btn" onClick={retryPendingShot} variant="outline" className="h-11 rounded-full border-[#E8E3D9] text-[#5C554F]">
                      <Camera size={16} className="mr-1.5" /> Riprova
                    </Button>
                    <Button data-testid="shot-discard-btn" onClick={discardPendingShot} variant="outline" className="h-11 rounded-full border-[#E8E3D9] text-red-600">
                      <X size={16} className="mr-1.5" /> Scarta
                    </Button>
                  </div>
                  <Button
                    data-testid="shot-finish-btn"
                    onClick={finishShotFlow}
                    className="w-full h-11 rounded-full bg-[#C25934] hover:bg-[#A64B2B] text-white font-semibold"
                  >
                    🏁 Termina ora ({shots.length + 1} {shots.length + 1 === 1 ? 'foto' : 'foto'}) · basta così
                  </Button>
                </div>
              )}

              {shotPhase === 'capture' && shots.length > 0 && (
                <Button
                  data-testid="shot-goto-validate-btn"
                  onClick={() => setShotPhase('validate')}
                  className="w-full h-12 rounded-full bg-[#C25934] hover:bg-[#A64B2B] text-white font-semibold"
                >
                  🏁 Termina ora ({shots.length} {shots.length === 1 ? 'foto' : 'foto'}) · procedi
                </Button>
              )}

              {shotPhase === 'validate' && (
                <Button
                  data-testid="submit-shot-flow-btn"
                  onClick={submitShotFlow}
                  disabled={uploading || shots.length === 0}
                  className="w-full h-12 rounded-full bg-[#C25934] hover:bg-[#A64B2B] text-white font-semibold"
                >
                  {uploading ? (
                    <><Loader2 size={18} className="mr-2 animate-spin" /> Elaborazione AI (~30-60s)...</>
                  ) : (
                    <>🚀 Elabora con AI</>
                  )}
                </Button>
              )}
            </div>
          </div>
        </div>
      )}
      {/* TAG ORDER DIALOG */}
      <Dialog open={tagOrderOpen} onOpenChange={setTagOrderOpen}>
        <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Outfit' }}>Ordina sezioni home</DialogTitle>
          </DialogHeader>
          <TagOrderManager onClose={() => setTagOrderOpen(false)} />
        </DialogContent>
      </Dialog>

      {/* RECOVERY DIALOG */}
      <RecoveryDialog
        open={recoveryOpen}
        onOpenChange={setRecoveryOpen}
        getHeaders={getHeaders}
        onMigrated={() => { fetchTodayFlyer(); fetchProducts(); }}
      />

      {/* STOCK EDITOR DIALOG */}
      <StockEditorDialog
        product={stockEditProduct}
        onClose={() => setStockEditProduct(null)}
        getHeaders={getHeaders}
        onSaved={fetchProducts}
      />

      {/* PRODUCT PREVIEW DIALOG */}
      <Dialog open={!!previewingProduct} onOpenChange={(o) => !o && setPreviewingProduct(null)}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto" data-testid="product-preview-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Outfit' }}>Anteprima prodotto</DialogTitle>
          </DialogHeader>
          {previewingProduct && (
            <div className="space-y-3">
              <ProductPreviewImages product={previewingProduct} />
              <div className="px-1">
                <h3 className="text-lg font-semibold text-[#2A2624]" style={{ fontFamily: 'Outfit' }}>
                  {previewingProduct.title}
                </h3>
                <p className="text-2xl font-bold text-[#C25934] mt-1">
                  €{(previewingProduct.price || 0).toFixed(2)}
                  {previewingProduct.original_price && (
                    <span className="text-sm text-[#877E78] line-through ml-2 font-normal">
                      €{previewingProduct.original_price.toFixed(2)}
                    </span>
                  )}
                </p>
                {previewingProduct.description && (
                  <p className="text-sm text-[#5C554F] mt-2 whitespace-pre-line">{previewingProduct.description}</p>
                )}
                {previewingProduct.tags?.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-2">
                    {previewingProduct.tags.map((t, i) => (
                      <span key={i} className="text-[10px] bg-[#F0EBE1] text-[#5C554F] px-2 py-0.5 rounded-full">#{t}</span>
                    ))}
                  </div>
                )}
                {previewingProduct.variants?.length > 0 && (
                  <div className="mt-3 space-y-1">
                    {previewingProduct.variants.map((v, i) => (
                      <p key={i} className="text-xs text-[#5C554F]">
                        <b>{v.type}:</b> {(v.options || []).join(' · ')}
                      </p>
                    ))}
                  </div>
                )}
              </div>
              <div className="flex gap-2 pt-2">
                <Button
                  data-testid="preview-edit-btn"
                  onClick={() => { openProductEdit(previewingProduct); setPreviewingProduct(null); }}
                  variant="outline"
                  className="flex-1 h-11 rounded-full border-[#E8E3D9]"
                >
                  ✏️ Modifica
                </Button>
                <a
                  data-testid="preview-open-public-btn"
                  href={`/prodotto/${previewingProduct.id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1 inline-flex items-center justify-center h-11 rounded-full bg-[#C25934] hover:bg-[#A64B2B] text-white text-sm font-medium"
                >
                  Apri pubblicamente ↗
                </a>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* PRODUCT EDIT DIALOG */}
      <Dialog open={!!editingProduct} onOpenChange={(o) => !o && setEditingProduct(null)}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto" data-testid="product-edit-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Outfit' }}>Modifica prodotto</DialogTitle>
          </DialogHeader>
          {editingProduct && (
            <div className="space-y-3">
              {/* IMAGE GALLERY + AI REGEN */}
              <EditProductImages
                product={editingProduct}
                getHeaders={getHeaders}
                onChange={(imgs) => setEditingProduct(p => ({ ...p, images: imgs }))}
                onRefresh={async () => {
                  try {
                    const { data } = await axios.get(`${API}/products/${editingProduct.id}`, { withCredentials: true, headers: getHeaders() });
                    setEditingProduct(data);
                  } catch (_) {}
                }}
              />

              <div>
                <label className="text-xs font-bold text-[#5C554F] uppercase">Titolo</label>
                <Input
                  data-testid="edit-prod-title"
                  value={editingProduct.title || ''}
                  onChange={e => setEditingProduct(p => ({ ...p, title: e.target.value }))}
                  className="h-11 mt-1 rounded-xl border-[#E8E3D9]"
                />
              </div>
              <div>
                <label className="text-xs font-bold text-[#5C554F] uppercase">Descrizione</label>
                <textarea
                  data-testid="edit-prod-desc"
                  rows={3}
                  value={editingProduct.description || ''}
                  onChange={e => setEditingProduct(p => ({ ...p, description: e.target.value }))}
                  className="w-full mt-1 px-3 py-2 rounded-xl border border-[#E8E3D9] bg-white text-sm resize-none outline-none focus:ring-2 focus:ring-[#C25934]"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-bold text-[#5C554F] uppercase">Prezzo (€)</label>
                  <Input
                    data-testid="edit-prod-price"
                    type="number" step="0.01"
                    value={editingProduct.price || ''}
                    onChange={e => setEditingProduct(p => ({ ...p, price: e.target.value }))}
                    className="h-11 mt-1 rounded-xl border-[#E8E3D9]"
                  />
                </div>
                <div>
                  <label className="text-xs font-bold text-[#5C554F] uppercase">Prezzo orig.</label>
                  <Input
                    data-testid="edit-prod-orig"
                    type="number" step="0.01"
                    value={editingProduct.original_price || ''}
                    onChange={e => setEditingProduct(p => ({ ...p, original_price: e.target.value }))}
                    className="h-11 mt-1 rounded-xl border-[#E8E3D9]"
                  />
                </div>
              </div>
              <div>
                <label className="text-xs font-bold text-[#5C554F] uppercase">Categoria</label>
                <Input
                  data-testid="edit-prod-cat"
                  value={editingProduct.category || ''}
                  onChange={e => setEditingProduct(p => ({ ...p, category: e.target.value }))}
                  className="h-11 mt-1 rounded-xl border-[#E8E3D9]"
                />
              </div>
              <div>
                <label className="text-xs font-bold text-[#5C554F] uppercase">Tag (separati da virgola)</label>
                <Input
                  data-testid="edit-prod-tags"
                  value={(editingProduct.tags || []).join(', ')}
                  onChange={e => setEditingProduct(p => ({ ...p, tags: e.target.value.split(',').map(t => t.trim().toLowerCase()).filter(Boolean) }))}
                  placeholder="es: biancheria, letto, cotone"
                  className="h-11 mt-1 rounded-xl border-[#E8E3D9]"
                />
              </div>
              <div className="flex gap-2 pt-2">
                <Button
                  data-testid="edit-cancel-btn"
                  onClick={() => setEditingProduct(null)}
                  variant="outline"
                  className="flex-1 h-11 rounded-full border-[#E8E3D9]"
                >
                  Annulla
                </Button>
                <Button
                  data-testid="edit-save-btn"
                  onClick={saveProductEdit}
                  className="flex-1 h-11 rounded-full bg-[#C25934] hover:bg-[#A64B2B] text-white"
                >
                  Salva
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function EditProductImages({ product, getHeaders, onChange, onRefresh }) {
  const [regenerating, setRegenerating] = React.useState(false);
  const [adding, setAdding] = React.useState(false);
  const fileRef = React.useRef(null);
  const images = product.images || [];

  const regenerate = async () => {
    setRegenerating(true);
    try {
      const fd = new FormData();
      const { data } = await axios.post(`${API}/products/${product.id}/regenerate-ai-images`, fd, {
        withCredentials: true,
        headers: { ...getHeaders(), 'Content-Type': 'multipart/form-data' },
        timeout: 240000,
      });
      onChange(data.images || []);
      toast.success('Nuove immagini AI generate!');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore generazione AI');
    } finally {
      setRegenerating(false);
    }
  };

  const addImage = async (file) => {
    if (!file) return;
    setAdding(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      await axios.post(`${API}/products/${product.id}/add-image`, fd, {
        withCredentials: true,
        headers: { ...getHeaders(), 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Immagine aggiunta');
      await onRefresh?.();
    } catch (_) {
      toast.error('Errore aggiunta immagine');
    } finally {
      setAdding(false);
    }
  };

  const deleteImage = async (idx) => {
    if (!window.confirm('Eliminare questa immagine?')) return;
    try {
      await axios.delete(`${API}/products/${product.id}/images/${idx}`, { withCredentials: true, headers: getHeaders() });
      await onRefresh?.();
      toast.success('Immagine eliminata');
    } catch (_) {
      toast.error('Errore eliminazione');
    }
  };

  return (
    <div className="space-y-2 rounded-xl border border-[#E8E3D9] p-2.5 bg-[#F0EBE1]/30">
      <div className="flex items-center justify-between">
        <span className="text-xs font-bold text-[#5C554F] uppercase">Immagini ({images.length})</span>
        <span className="text-[10px] text-[#877E78]">Clicca per ingrandire · X per eliminare</span>
      </div>

      {images.length > 0 ? (
        <div className="flex gap-2 overflow-x-auto pb-1">
          {images.map((img, i) => (
            <DraftImageThumb key={`${img?.storage_path || i}-${i}`} img={img} onDelete={() => deleteImage(i)} />
          ))}
        </div>
      ) : (
        <p className="text-xs text-[#877E78] text-center py-3">Nessuna immagine. Aggiungine una o genera con AI.</p>
      )}

      <div className="flex gap-2 pt-1">
        <Button
          data-testid="edit-add-image-btn"
          type="button"
          onClick={() => fileRef.current?.click()}
          disabled={adding}
          variant="outline"
          size="sm"
          className="flex-1 h-9 rounded-full border-[#C25934]/40 text-[#C25934] text-xs"
        >
          {adding ? <Loader2 size={12} className="mr-1 animate-spin" /> : <Plus size={12} className="mr-1" />}
          Aggiungi foto
        </Button>
        <Button
          data-testid="edit-regen-ai-btn"
          type="button"
          onClick={regenerate}
          disabled={regenerating}
          size="sm"
          className="flex-1 h-9 rounded-full bg-[#C25934] hover:bg-[#A64B2B] text-white text-xs"
        >
          {regenerating ? <Loader2 size={12} className="mr-1 animate-spin" /> : '🤖'}
          Genera nuova AI
        </Button>
      </div>

      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={e => e.target.files?.[0] && addImage(e.target.files[0])}
      />
    </div>
  );
}

function ProductPreviewImages({ product }) {
  const [src, setSrc] = React.useState(null);
  React.useEffect(() => {
    const img = product.images?.[0];
    if (!img) return;
    const imgData = typeof img === 'string' ? { storage_path: img } : img;
    if (imgData.external_url) { setSrc(imgData.external_url); return; }
    const path = imgData.storage_path;
    if (!path) return;
    let alive = true;
    axios.get(`${API}/files/${path}`, { responseType: 'blob' })
      .then(res => { if (alive) setSrc(URL.createObjectURL(res.data)); })
      .catch(() => {});
    return () => { alive = false; };
  }, [product]);

  return (
    <div className="rounded-2xl overflow-hidden bg-[#F0EBE1] aspect-square">
      {src ? (
        <img src={src} alt={product.title} className="w-full h-full object-cover" />
      ) : (
        <div className="w-full h-full flex items-center justify-center">
          <Loader2 size={24} className="animate-spin text-[#877E78]" />
        </div>
      )}
    </div>
  );
}


function DraftImageThumb({ img, onDelete }) {
  const [src, setSrc] = React.useState(null);
  const [zoomed, setZoomed] = React.useState(false);
  React.useEffect(() => {
    const imgData = typeof img === 'string' ? { storage_path: img } : img;
    if (imgData.external_url) { setSrc(imgData.external_url); return; }
    const path = imgData.storage_path;
    if (!path) return;
    axios.get(`${API}/files/${path}`, { responseType: 'blob' })
      .then(res => setSrc(URL.createObjectURL(res.data)))
      .catch(() => {});
  }, [img]);

  return (
    <>
      <div
        className="relative flex-shrink-0 w-24 h-24 rounded-xl overflow-hidden bg-[#F0EBE1] border border-[#E8E3D9] cursor-zoom-in group"
        onClick={() => src && setZoomed(true)}
        data-testid="draft-image-thumb"
      >
        {src ? (
          <img src={src} alt="" className="w-full h-full object-cover transition-transform group-hover:scale-105" />
        ) : (
          <div className="w-full h-full flex items-center justify-center"><Loader2 size={16} className="animate-spin text-[#877E78]" /></div>
        )}
        <button
          onClick={(e) => { e.stopPropagation(); onDelete(); }}
          className="absolute top-1 right-1 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center text-xs hover:bg-red-600"
          data-testid="draft-image-delete-btn"
        >
          <X size={12} />
        </button>
        <span className="absolute bottom-0 left-0 right-0 bg-black/50 text-white text-[8px] text-center py-0.5">
          {img?.type || '?'}
        </span>
      </div>

      {zoomed && src && (
        <div
          data-testid="draft-image-lightbox"
          className="fixed inset-0 z-[9999] bg-black/85 flex items-center justify-center p-4 cursor-zoom-out"
          onClick={() => setZoomed(false)}
        >
          <button
            data-testid="draft-image-lightbox-close"
            onClick={() => setZoomed(false)}
            className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center"
          >
            <X size={22} />
          </button>
          <img
            src={src}
            alt=""
            className="max-w-full max-h-full object-contain rounded-lg shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          />
          {img?.type && (
            <span className="absolute bottom-6 left-1/2 -translate-x-1/2 bg-black/60 text-white text-xs px-3 py-1.5 rounded-full uppercase tracking-wider">
              {img.type}
            </span>
          )}
        </div>
      )}
    </>
  );
}

function ProductAdminCard({ product, shop, onTogglePublish, onDelete, onAddImage, onPreview, onEdit, onAddToFlyer, onEditStock }) {
  const [imgSrc, setImgSrc] = useState(null);
  const addImgRef = useRef(null);

  useEffect(() => {
    const img = product.images?.[0];
    if (!img) return;
    const imgData = typeof img === 'string' ? { storage_path: img } : img;
    if (imgData.external_url) { setImgSrc(imgData.external_url); return; }
    const path = imgData.storage_path;
    if (!path) return;
    axios.get(`${API}/files/${path}`, { responseType: 'blob' })
      .then(res => setImgSrc(URL.createObjectURL(res.data)))
      .catch(() => {});
    return () => { if (imgSrc && imgSrc.startsWith('blob:')) URL.revokeObjectURL(imgSrc); };
  }, [product]);

  const productUrl = `${window.location.origin}/prodotto/${product.id}`;

  return (
    <div data-testid={`admin-product-${product.id}`} className="bg-white rounded-xl border border-[#E8E3D9] p-3">
      <div className="flex gap-3">
        <div className="w-16 h-16 rounded-lg overflow-hidden bg-[#F0EBE1] flex-shrink-0">
          {imgSrc ? (
            <img src={imgSrc} alt="" className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <ImageIcon size={20} className="text-[#877E78]" />
            </div>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <h4 className="font-medium text-sm text-[#2A2624] truncate" style={{ fontFamily: 'Outfit' }}>
            {product.title}
          </h4>
          <p className="text-[#C25934] font-bold text-sm">{product.price?.toFixed(2)} &euro;</p>
          <div className="flex items-center gap-1.5 mt-1 flex-wrap">
            <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
              product.status === 'published' ? 'bg-[#4A6B53]/10 text-[#4A6B53]' : 'bg-[#D98324]/10 text-[#D98324]'
            }`}>
              {product.status === 'published' ? 'Pubblicato' : 'Bozza'}
            </span>
            <span className="text-[10px] text-[#877E78]">{product.images?.length || 0} foto</span>
            {product.category && (
              <span className="text-[10px] text-[#877E78] truncate max-w-[80px]">· {product.category}</span>
            )}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-1.5 mt-3">
        <button
          data-testid={`preview-${product.id}`}
          onClick={() => onPreview?.(product)}
          className="flex items-center justify-center gap-1 px-2 py-1.5 rounded-lg text-[11px] font-medium bg-[#F0EBE1] text-[#5C554F] hover:bg-[#E6E2D6]"
        >
          <Eye size={12} /> Anteprima
        </button>
        <button
          data-testid={`edit-${product.id}`}
          onClick={() => onEdit?.(product)}
          className="flex items-center justify-center gap-1 px-2 py-1.5 rounded-lg text-[11px] font-medium bg-[#0088cc]/10 text-[#0088cc] hover:bg-[#0088cc]/20"
        >
          ✏️ Modifica
        </button>
        <button
          data-testid={`add-to-flyer-${product.id}`}
          onClick={() => onAddToFlyer?.(product.id)}
          className="flex items-center justify-center gap-1 px-2 py-1.5 rounded-lg text-[11px] font-medium bg-[#C25934]/10 text-[#C25934] hover:bg-[#C25934]/20"
          title="Aggiungi al volantino di oggi"
        >
          📰 Volantino
        </button>
      </div>

      <div className="flex items-center gap-2 mt-2">
        <button
          data-testid={`toggle-publish-${product.id}`}
          onClick={() => onTogglePublish(product)}
          className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[11px] font-medium bg-[#F0EBE1] text-[#5C554F] hover:bg-[#E6E2D6] transition-colors"
        >
          {product.status === 'published' ? <EyeOff size={12} /> : <Eye size={12} />}
          {product.status === 'published' ? 'Nascondi' : 'Pubblica'}
        </button>
        <button
          data-testid={`edit-stock-${product.id}`}
          onClick={() => onEditStock?.(product)}
          className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[11px] font-medium bg-[#4A6B53]/10 text-[#4A6B53] hover:bg-[#4A6B53]/20 transition-colors"
          title="Gestisci scorte"
        >
          📦 Stock
          {product.stock?.track_inventory && (() => {
            const s = product.stock || {};
            const total = s.per_variant?.length > 0
              ? s.per_variant.reduce((sum, v) => sum + (v.qty || 0), 0)
              : (s.total || 0);
            return <span className="ml-0.5 font-bold">{total}</span>;
          })()}
        </button>
        <label className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[11px] font-medium bg-[#F0EBE1] text-[#5C554F] hover:bg-[#E6E2D6] transition-colors cursor-pointer">
          <Plus size={12} /> Foto
          <input
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(e) => {
              if (e.target.files?.[0]) onAddImage(product.id, e.target.files[0]);
            }}
          />
        </label>
        <button
          data-testid={`delete-btn-${product.id}`}
          onClick={() => onDelete(product.id)}
          className="ml-auto p-1.5 rounded-lg text-red-500 hover:bg-red-50 transition-colors"
        >
          <Trash2 size={14} />
        </button>
      </div>

      {product.status === 'published' && (
        <div className="mt-3 pt-3 border-t border-[#E8E3D9]">
          <ShareButtons product={product} productUrl={productUrl} shop={shop} kind="product" resourceId={product.id} />
        </div>
      )}
    </div>
  );
}
